import { useEffect, useRef, useState } from "react";
import { Reveal, MaskedLine } from "@/components/Reveal";

const W = 1350;
const H = 1800;
const COLORS = ["#FF10F0", "#00F0FF", "#FFE600"];

const ART_STICKERS = {
  leopardcroc: { label: "LEOPARD & CROC", src: "/images/stickers/sticker-leopardcroc.webp" },
  chaos: { label: "FULL CHAOS", src: "/images/stickers/sticker-chaos.webp" },
  foot: { label: "FOOT BADGE", src: "/images/stickers/sticker-foot.webp" },
  speakwild: { label: "SPEAK WILD", src: "/images/stickers/sticker-speakwild.webp" },
  shutupart: { label: "शटअप.", src: "/images/stickers/sticker-shutup.webp" },
};

const STICKERS = [
  { id: "shutup", label: "SHUT UP." },
  { id: "behave", label: "DON'T BEHAVE." },
  { id: "hindi", label: "स्पीक वाइल्ड" },
  { id: "leopardcroc", label: "LEOPARD & CROC" },
  { id: "chaos", label: "FULL CHAOS" },
  { id: "foot", label: "FOOT BADGE" },
  { id: "speakwild", label: "SPEAK WILD" },
  { id: "shutupart", label: "शटअप." },
  { id: "splat", label: "SPRAY" },
  { id: "own", label: "YOUR REBEL LINE" },
  { id: "custom", label: "YOUR DESIGN" },
];

function paintBase(g) {
  g.fillStyle = "#0D0D0D";
  g.fillRect(0, 0, W, H);
  document.fonts?.load("225px Anton").then(() => {
    g.save();
    g.globalAlpha = 0.07;
    g.fillStyle = "#F7F5F0";
    g.textAlign = "center";
    g.font = "255px Anton";
    g.fillText("SHUTUP.", W / 2, H / 2 + 45);
    g.restore();
  });
}

export default function PosterMaker() {
  const canvasRef = useRef(null);
  const artRef = useRef({});
  const customRef = useRef(null);
  const [sticker, setSticker] = useState("shutup");
  const [count, setCount] = useState(0);
  const [ownLine, setOwnLine] = useState("");
  const [customUrl, setCustomUrl] = useState(null);

  useEffect(() => {
    paintBase(canvasRef.current.getContext("2d"));
    document.fonts?.load("96px 'Bakbak One'");
    Object.entries(ART_STICKERS).forEach(([id, s]) => {
      const img = new Image();
      img.src = s.src;
      artRef.current[id] = img;
    });
  }, []);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        customRef.current = img;
        setCustomUrl(reader.result);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const stamp = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    const y = ((e.clientY - r.top) / r.height) * H;
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    const rot = (Math.random() - 0.5) * 0.7;
    const scale = 0.8 + Math.random() * 0.7;

    if (sticker === "custom" && !customRef.current) return;

    const paint = () => {
      const g = c.getContext("2d");
      g.save();
      g.translate(x, y);
      g.rotate(rot);
      g.scale(scale, scale);

      if (sticker === "splat") {
        g.fillStyle = color;
        for (let i = 0; i < 34; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * 110;
          g.beginPath();
          g.arc(Math.cos(a) * d, Math.sin(a) * d, 3 + Math.random() * 10, 0, 7);
          g.fill();
        }
      } else if (ART_STICKERS[sticker] || sticker === "custom") {
        const img = sticker === "custom" ? customRef.current : artRef.current[sticker];
        const s = 380 / Math.max(img.naturalWidth, img.naturalHeight);
        const w = img.naturalWidth * s;
        const h = img.naturalHeight * s;
        g.drawImage(img, -w / 2, -h / 2, w, h);
      } else {
        const text =
          sticker === "shutup"
            ? "SHUT UP."
            : sticker === "behave"
              ? "DON'T BEHAVE."
              : sticker === "hindi"
                ? "स्पीक वाइल्ड"
                : (ownLine.trim() || "SAY IT BACK.").toUpperCase().slice(0, 26);
        g.textAlign = "center";
        g.font = sticker === "hindi" ? "96px 'Bakbak One'" : sticker === "own" ? "81px Anton" : "108px Anton";
        g.lineWidth = 13;
        g.strokeStyle = "#0D0D0D";
        g.strokeText(text, 0, 0);
        g.fillStyle = color;
        g.fillText(text, 0, 0);
      }
      g.restore();
      setCount((n) => n + 1);
    };

    if (ART_STICKERS[sticker]) {
      const img = artRef.current[sticker];
      if (img?.complete && img.naturalWidth) paint();
      else img?.addEventListener("load", paint, { once: true });
      return;
    }
    paint();
  };

  const clear = () => {
    paintBase(canvasRef.current.getContext("2d"));
    setCount(0);
  };

  const download = () => {
    const a = document.createElement("a");
    a.download = "shutup-poster.png";
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
  };

  return (
    <section data-testid="poster-section" className="bg-ink text-paper py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal>
              <p className="font-sans text-acid text-xs font-semibold tracking-[0.35em] mb-6">SAY IT BACK</p>
            </Reveal>
            <h2 className="font-display leading-[0.85]">
              <MaskedLine inView className="text-[clamp(2.6rem,8vw,6.5rem)]">
                STAMP THE <span className="text-pink">POSTER.</span>
              </MaskedLine>
            </h2>
          </div>
          <p className="font-hand text-turq text-2xl sm:text-3xl rotate-1 max-w-xs">
            make it yours. download the evidence.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-10 mt-14 items-start">
          <Reveal>
            <div className="relative border-2 border-paper/20 max-w-xl mx-auto lg:mx-0" data-cursor="shutup">
              <canvas
                ref={canvasRef}
                width={W}
                height={H}
                onClick={stamp}
                data-testid="poster-canvas"
                className="w-full h-auto block touch-manipulation"
              />
              <span className="absolute top-3 left-3 bg-black/70 font-sans text-[10px] font-semibold tracking-[0.25em] px-2 py-1" data-testid="poster-count">
                STICKERS: {count}
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="font-sans text-paper/50 text-xs font-semibold tracking-[0.3em] mb-4">PICK A STICKER</p>
            <div className="flex flex-wrap gap-2" data-testid="sticker-tray">
              {STICKERS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`sticker-${s.id}`}
                  onClick={() => setSticker(s.id)}
                  className={`font-sans font-bold text-xs sm:text-sm tracking-[0.12em] px-4 py-3 border-2 transition-colors ${
                    sticker === s.id
                      ? "bg-acid text-black border-acid"
                      : "border-paper/30 text-paper hover:border-pink"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {sticker === "own" && (
              <div className="mt-5" data-testid="own-line-wrap">
                <p className="font-sans text-paper/50 text-[10px] font-semibold tracking-[0.3em] mb-2">
                  TYPE YOUR REBEL LINE — 26 CHARACTERS OF TRUTH
                </p>
                <input
                  value={ownLine}
                  onChange={(e) => setOwnLine(e.target.value)}
                  maxLength={26}
                  data-testid="own-line-input"
                  placeholder="tell them what you never said…"
                  className="w-full border-2 border-acid bg-transparent px-4 py-3.5 font-hand text-2xl text-paper outline-none placeholder:text-paper/30"
                />
              </div>
            )}

            {sticker === "custom" && (
              <div className="mt-5" data-testid="custom-design-wrap">
                <p className="font-sans text-paper/50 text-[10px] font-semibold tracking-[0.3em] mb-2">
                  UPLOAD YOUR OWN DESIGN — TRANSPARENT PNGs LOOK BEST
                </p>
                <label
                  className="flex items-center gap-4 border-2 border-dashed border-turq px-4 py-4 cursor-pointer hover:border-acid transition-colors"
                  data-testid="custom-design-drop"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    data-testid="custom-design-input"
                    className="hidden"
                  />
                  {customUrl ? (
                    <>
                      <img src={customUrl} alt="Your design" data-testid="custom-design-preview" className="w-14 h-14 object-contain bg-black/40" />
                      <span className="font-hand text-2xl text-turq -rotate-1">loaded. go stamp it.</span>
                    </>
                  ) : (
                    <span className="font-hand text-2xl text-paper/60">+ drop your art here</span>
                  )}
                </label>
              </div>
            )}

            <div className="flex gap-3 mt-8">
              <button
                data-testid="poster-clear"
                onClick={clear}
                className="flex-1 font-sans font-bold text-xs tracking-[0.2em] px-5 py-4 border-2 border-paper/40 hover:border-blood hover:text-blood transition-colors"
              >
                BURN IT
              </button>
              <button
                data-testid="poster-download"
                onClick={download}
                data-cursor="shutup"
                className="flex-1 font-sans font-bold text-xs tracking-[0.2em] px-5 py-4 bg-paper text-black hover:bg-pink transition-colors"
              >
                DOWNLOAD ↓
              </button>
            </div>
            <p className="font-sans text-paper/40 text-[11px] tracking-[0.2em] mt-6 leading-relaxed">
              CLICK THE POSTER TO STAMP. EVERY STAMP LANDS DIFFERENTLY — ANGLE, SIZE AND COLOUR ARE OUT OF YOUR
              HANDS. LIKE MOST THINGS.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
